<template>
  <div class="tw-relative">
    <div class="tw-grid tw-grid-cols-12 max-lg:tw-grid-cols-1 tw-gap-6 tw-pb-8">
      <div class="tw-col-span-7 tw-h-full tw-flex tw-items-center">
        <!-- Usage chart -->
        <div class="tw-w-full tw-h-full tw-rounded-sm tw-bg-white tw-p-4">
          <div class="tw-grid tw-grid-cols-5 tw-gap-3">
            <div class="tw-col-start-1 tw-col-end-3">
              {{ t("solution-label") }}
            </div>
            <div class="tw-col-start-1 tw-col-end-3 tw-pb-1.5">
              <cl-select
                v-model="selectedServiceUuid"
                test-id="msp-license-usage-select-solution"
                :is-disabled="filteredServices.length <= 1"
                @update:modelValue="selectService"
              >
                <cl-select-item
                  v-for="option in filteredServices"
                  :value="option.value"
                  :text-value="option.text"
                >
                  {{ option.text }}</cl-select-item
                >
              </cl-select>
            </div>
          </div>
          <usage-chart
            :id="selectedMsp?.uuid || selectedAccount?.uuid"
            :selected-service-licence-uuid="selectedServiceLicenceUuid"
            data-test-id="msp-license-usage-usage-chart"
          />
        </div>
      </div>
      <div
        class="tw-col-span-5 max-lg:tw-col-span-7 tw-h-full tw-flex tw-flex-col tw-space-y-6"
      >
        <usage-per-solution
          data-test-id="msp-license-usage-usage-per-solution"
        />
        <div class="tw-max-h-56">
          <usage-per-customer
            :selected-service-uuid="selectedServiceUuid"
            :selected-solution-name="selectedSolutionName"
            data-test-id="msp-license-usage-usage-per-customer"
          />
        </div>
      </div>
    </div>
    <header>
      <cl-heading :size="'h4'">
        {{ t("all-customers-menu-item") }}
      </cl-heading>
    </header>
    <customers-list :selected-service-uuid="selectedServiceUuid" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useI18n } from "vue-i18n";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { ALL_SERVICES } from "@/utils/services.ts";
import Cookies from "universal-cookie";
import { CHART_COOKIE } from "@/constants/chart-cookie";

import UsageChart from "@/modules/Usage/views/Msp/Overview/UsageChart/index.vue";
import UsagePerCustomer from "@/modules/Usage/views/Msp/Overview/UsagePerCustomer/index.vue";
import UsagePerSolution from "@/components/UsagePerSolution/index.vue";
import CustomersList from "@/modules/Usage/views/Msp/Overview/CustomersList/index.vue";
import { ServiceOption } from "@/types/serviceUsage.ts";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks.ts";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const pageMetaStore = usePageMetaStore();
const loading = ref<boolean>(true);

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);

const selectedServiceUuid = ref<string>("");
const selectedSolutionName = ref<string>("");
const selectedServiceLicenceUuid = ref<string>("");
const allServices = ref<ServiceOption[]>([]);
const filteredServices = ref<ServiceOption[]>([]);

const selectService = async (serviceUuid: string) => {
  const selectedService = filteredServices.value.find(
    (service: ServiceOption) => service.value === serviceUuid,
  );

  selectedServiceUuid.value = selectedService?.value ?? "";
  selectedSolutionName.value = selectedService?.text ?? "";
  selectedServiceLicenceUuid.value = selectedService?.licence_uuid ?? "";

  const cookies = new Cookies();
  cookies.set(
    `${CHART_COOKIE.MSP_USAGE_CHART_SELECTED_SERVICE}_${selectedMsp.value?.uuid || selectedAccount.value?.uuid}`,
    serviceUuid,
  );
};

const getOrganisationServices = async () => {
  loading.value = true;
  const organisationResponse = (await organisationsStore.showOrg({
    uuid: selectedMsp.value?.uuid || selectedAccount.value?.uuid || "",
  }))!;

  if (organisationResponse.data.licence?.solutions?.length) {
    filteredServices.value = allServices.value
      .filter((service: ServiceOption) =>
        organisationResponse.data.licence.solutions.some(
          (mspService: { solution_uuid: string }) =>
            mspService.solution_uuid === service.value,
        ),
      )
      .map((service: ServiceOption) => {
        const matchingService =
          organisationResponse.data.licence.solutions.find(
            (mspService: { solution_uuid: string }) =>
              mspService.solution_uuid === service.value,
          );
        return {
          ...service,
          licence_uuid: matchingService?.uuid,
          value: matchingService?.solution_uuid,
        };
      });
  }
  loading.value = false;
};

onMounted(async () => {
  pageMetaStore.setPageTitle({
    title: t("license-usage"),
    info: {
      docsLink: EXTERNAL_DOCS_LINKS.MSP_LICENCE_USAGE_OVERVIEW,
      text: t("license-usage-page"),
    },
  });

  allServices.value = ALL_SERVICES.map(
    (service: { uuid: string; menuItemLabel: string }) => {
      return {
        value: service.uuid,
        text: t(service.menuItemLabel),
      };
    },
  );

  await getOrganisationServices();

  const cookies = new Cookies();
  const cookieValue = cookies.get(
    `${CHART_COOKIE.MSP_USAGE_CHART_SELECTED_SERVICE}_${selectedMsp.value?.uuid || selectedAccount.value?.uuid}`,
  );

  const cookieSelectedService = filteredServices.value.find(
    (service: ServiceOption) => service.value === cookieValue,
  );

  const selectedService = cookieSelectedService ?? filteredServices.value[0];

  if (!selectedService) {
    selectedServiceUuid.value = "";
    selectedSolutionName.value = "";
    selectedServiceLicenceUuid.value = "";
    return;
  }

  selectedServiceUuid.value = selectedService.value ?? "";
  selectedSolutionName.value = selectedService.text ?? "";
  selectedServiceLicenceUuid.value = selectedService.licence_uuid ?? "";
});
</script>
